import { LayerFilterConfig } from './types';

/* ── Map defaults ── */
export const DEFAULT_CENTER: [number, number] = [-37.78, 145.13];
export const DEFAULT_ZOOM = 13;

/* ── Basemap tile providers ── */
export const BASEMAPS = {
  Street: {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  },
  Satellite: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: '&copy; Esri, Maxar, Earthstar Geographics',
  },
  Topo: {
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: '&copy; OpenTopoMap contributors',
  },
  Dark: {
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; <a href="https://carto.com/">CARTO</a>',
  },
} as const;

export type BasemapKey = keyof typeof BASEMAPS;

/* ── Choropleth color scale (5-step green gradient) ── */
export const CHOROPLETH_COLORS = [
  '#d9f4d7',
  '#a4d99b',
  '#55a655',
  '#2e873d',
  '#0d3a1c',
];

/* ── Quick-filter chips per layer code ── */
export const LAYER_FILTERS: LayerFilterConfig = {
  facilities: [
    { label: 'Libraries', field: 'facility_type', value: 'Library' },
    { label: 'Aquatic', field: 'facility_type', value: 'Aquatic Centre' },
    { label: 'Community', field: 'facility_type', value: 'Community Centre' },
    { label: 'Sports', field: 'facility_type', value: 'Sports Complex' },
  ],
  parks: [
    { label: 'Bushland', field: 'park_type', value: 'Bushland' },
    { label: 'Sports', field: 'park_type', value: 'Sports' },
    { label: 'Heritage', field: 'park_type', value: 'Heritage' },
    { label: 'Community', field: 'park_type', value: 'Community' },
  ],
  trees: [
    { label: 'Eucalyptus', field: 'genus', value: 'Eucalyptus' },
    { label: 'Corymbia', field: 'genus', value: 'Corymbia' },
    { label: 'Ulmus', field: 'genus', value: 'Ulmus' },
  ],
  playgrounds: [
    { label: 'All Ages', field: 'age_group', value: 'All ages' },
    { label: 'Toddler', field: 'age_group', value: '2-8 years' },
    { label: 'Accessible', field: 'accessible', value: 'true' },
  ],
  toilets: [
    { label: 'Automated', field: 'toilet_type', value: 'Automated' },
    { label: 'Accessible', field: 'accessible', value: 'true' },
    { label: '24/7', field: 'hours', value: '24/7' },
  ],
  schools: [
    { label: 'Primary', field: 'school_type', value: 'Primary' },
    { label: 'Secondary', field: 'school_type', value: 'Secondary' },
    { label: 'Government', field: 'sector', value: 'Government' },
    { label: 'Independent', field: 'sector', value: 'Independent' },
  ],
  heritage: [
    { label: 'Building', field: 'heritage_type', value: 'Building' },
    { label: 'Streetscape', field: 'heritage_type', value: 'Streetscape' },
    { label: 'State', field: 'significance', value: 'State' },
  ],
  carparks: [
    { label: 'Multi-level', field: 'parking_type', value: 'Multi-level' },
    { label: 'Free', field: 'fee', value: 'Free' },
    { label: 'EV Charging', field: 'ev_charging', value: 'true' },
  ],
  cycling: [
    { label: 'Trails', field: 'infra_type', value: 'Shared trail' },
    { label: 'Bike Parking', field: 'infra_type', value: 'Bike parking' },
    { label: 'Repair', field: 'infra_type', value: 'Repair station' },
  ],
  dog_offleash: [
    { label: 'Full-time', field: 'offleash_type', value: 'Full-time' },
    { label: 'Fenced', field: 'fencing', value: 'Partial' },
    { label: 'Water', field: 'water', value: 'Tap' },
  ],
  public_art: [
    { label: 'Sculpture', field: 'art_type', value: 'Sculpture' },
    { label: 'Mural', field: 'art_type', value: 'Mural' },
    { label: 'Memorial', field: 'art_type', value: 'Memorial' },
  ],
  ev_charging: [
    { label: 'Fast DC', field: 'charger_type', value: 'DC Fast' },
    { label: 'Level 2', field: 'charger_type', value: 'AC Level 2' },
    { label: 'Operational', field: 'status', value: 'Operational' },
  ],
};

/* ── Property keys hidden from detail panel ── */
export const HIDDEN_DETAIL_KEYS = new Set([
  'feature_id', 'name', 'description', 'layer_name', 'layer_code', 'layer_color', 'layer_type',
]);
